/**
 * FunnelChartViews — the added funnel representations from the "Funnel Builder" redesign:
 *   • Bars     — horizontal stepped bars with a hatched "off-path" ghost tail
 *   • Vertical — vertical bar chart (columns), recharts; also the >8-step fallback
 *
 * The "Stream" option keeps the existing Nivo FunnelFlowChart (prod), so its shape is
 * unchanged; this file only powers Bars/Vertical. Steps sit on the accent tint and
 * only the step after the biggest drop takes orange (Paper chart system, Oct 2026:
 * funnel steps never walk the chart ramp). Off-path / cross-domain read muted.
 */
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Tooltip, XAxis, YAxis } from "recharts";
import { formatNumeric } from "@/lib/utils/stringUtils";
import { cn } from "@/lib/utils";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowUpRight01Icon } from "@hugeicons/core-free-icons";

export type FunnelChartStyle = "steps" | "stream" | "bars" | "list";

/** Mirrors FunnelStudio's StepType / FunnelFlowChart's StepEventType. */
export type FunnelChartStepType = "page_view" | "click" | "form_submission";

export interface FunnelChartStep {
  id: string | number;
  name: string;
  /** sessions that reached this step via the funnel path, in order */
  inFunnel: number;
  /** everyone who hit this step's target(s) any way (derived from suggestions) */
  matches: number;
  /** total events (deduplicated by event_id) for in-funnel sessions */
  totalEvents?: number;
  /** total events for ALL sessions (in-funnel + off-path) — from unbounded query */
  matchedEvents?: number;
  /** host + path (page) or human event label, for the secondary line */
  targetLabel: string;
  /** number of OR conditions on the step (to show "+N") */
  conditionCount: number;
  /** true when this step's property differs from the previous step's */
  crosses: boolean;
  /** step's event type — drives the "unique visitors" vs "unique visitors submitted" copy */
  type?: FunnelChartStepType;
  /** "caution" marks a branch/exit step so it doesn't read as a sequential conversion */
  tone?: "default" | "caution";
}

/** "unique visitors submitted" for form steps, "unique visitors" for everything else. */
function visitorsPhrase(type: FunnelChartStepType | undefined, noun = "unique visitors"): string {
  return type === "form_submission" ? `${noun} submitted` : noun;
}

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * The step after the funnel's biggest drop (by rate): the one value per view
 * that takes orange (Paper "Funnels · Detail": the sign-up form bar). Every
 * other step sits on the accent tint; funnel steps never walk the chart ramp.
 */
export function biggestDropIndex(steps: Pick<FunnelChartStep, "inFunnel" | "tone">[]): number | null {
  let best: { i: number; rate: number } | null = null
  for (let i = 1; i < steps.length; i++) {
    const prev = steps[i - 1].inFunnel
    if (prev <= 0 || steps[i].tone === "caution") continue
    const rate = Math.max(prev - steps[i].inFunnel, 0) / prev
    if (rate > 0 && (!best || rate > best.rate)) best = { i, rate }
  }
  return best ? best.i : null
}

function pct(n: number, d: number): string {
  if (!d || !Number.isFinite(n) || !Number.isFinite(d)) return "0%";
  const v = (n / d) * 100;
  if (!Number.isFinite(v)) return "0%";
  if (v >= 99.5) return "100%";
  if (v >= 10) return Math.round(v) + "%";
  return v.toFixed(1) + "%";
}

/** Neutral continued-% chip — severity is carried by the bar shape, not color (DESIGN.md). */
function ContinuedChip({ value }: { value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted/60 px-2 py-px text-[10.5px] font-semibold tabular-nums text-foreground">
      {value} continued
    </span>
  );
}

// ───────────────────────────────────────────────────────────────── STEPS ──
/** Paper "Funnels · New funnel" preview: one tint-ring bar per step, drop-off between. */
function StepsChart({ steps, entered }: { steps: FunnelChartStep[]; entered: number }) {
  const keyIndex = biggestDropIndex(steps);
  return (
    <div className="flex flex-col gap-3.5">
      {steps.map((s, i) => {
        const prev = i > 0 ? steps[i - 1].inFunnel : 0;
        const dropped = Math.max(prev - s.inFunnel, 0);
        const fill = entered ? Math.min((s.inFunnel / entered) * 100, 100) : 0;
        return (
          <div key={s.id} className="flex flex-col gap-3.5">
            {i > 0 && dropped > 0 && (
              <p
                className={cn(
                  "text-xs leading-4 tabular-nums",
                  i === keyIndex ? "font-medium text-primary-strong" : "text-muted-foreground",
                )}
              >
                {pct(dropped, prev)} drop off · {formatNumeric(dropped)} {s.type === "form_submission" ? "didn't submit" : "visitors"}
              </p>
            )}
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between gap-3 text-[13px] leading-4">
                <span className="flex min-w-0 items-center gap-1.5 font-medium">
                  <span className="truncate">
                    {i + 1} · {s.targetLabel || s.name}
                  </span>
                  {s.crosses && <HugeiconsIcon icon={ArrowUpRight01Icon} size={11} className="shrink-0 text-foreground/50" />}
                </span>
                <span className="shrink-0 font-semibold tabular-nums">{formatNumeric(s.inFunnel)}</span>
              </div>
              <div className="flex h-7 rounded-md bg-muted">
                <div
                  className={cn(
                    "h-7 rounded-md transition-[width] duration-500",
                    s.tone === "caution" ? "bg-foreground/15" : i === keyIndex ? "bg-primary" : "bg-primary-tint-ring",
                  )}
                  style={{ width: `${Math.max(fill, s.inFunnel > 0 ? 1.5 : 0)}%` }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────── BARS ──
function BarsChart({
  steps,
  entered,
  visitorNoun,
}: {
  steps: FunnelChartStep[];
  entered: number;
  visitorNoun?: string;
}) {
  const n = steps.length;
  const keyIndex = biggestDropIndex(steps);
  return (
    <div className="flex flex-col">
      {steps.map((s, i) => {
        const fillPct = entered ? (s.inFunnel / entered) * 100 : 0;
        const offPath = Math.max(s.matches - s.inFunnel, 0);
        const ghostPct = entered ? Math.min((offPath / entered) * 100, 100 - fillPct) : 0;
        const prev = i > 0 ? steps[i - 1].inFunnel : s.inFunnel;
        return (
          <div key={s.id}>
            <div className="flex items-center gap-2 px-0.5 pt-1.5 pb-3.5">
              <span className="flex size-5 items-center justify-center rounded-full bg-muted/70 text-[10px] font-semibold text-muted-foreground">
                {i + 1}
              </span>
              <span className="text-[11px] font-semibold text-foreground">{s.name}</span>
              <span
                className={cn(
                  "truncate font-mono text-[11px]",
                  s.tone === "caution"
                    ? "text-warning dark:text-warning"
                    : "text-muted-foreground"
                )}
              >
                {s.targetLabel}
              </span>
              {s.crosses && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground">
                  <HugeiconsIcon icon={ArrowUpRight01Icon} size={11} /> cross-domain
                </span>
              )}
              <span className="ml-auto text-[11px] tabular-nums">
                {i === 0 ? (
                  <span className="text-muted-foreground">entry point</span>
                ) : (
                  <ContinuedChip value={pct(s.inFunnel, prev)} />
                )}
              </span>
            </div>
            <div className="relative flex h-14 items-stretch">
              <div
                className={cn(
                  "relative flex flex-col items-start justify-center overflow-hidden rounded-md px-3.5 transition-[width] duration-500",
                  s.tone === "caution"
                    ? "bg-foreground/15 text-foreground"
                    : i === keyIndex
                      ? "bg-primary text-primary-foreground"
                      : "bg-primary-tint-ring text-foreground",
                )}
                style={{ width: `max(${fillPct}%, 64px)` }}
              >
                <span className="text-base font-semibold leading-none tabular-nums">{formatNumeric(s.inFunnel)}</span>
                <span className="mt-0.5 whitespace-nowrap text-[10px] font-medium opacity-85">
                  {s.type === "form_submission" ? "submitted" : "in funnel"}
                </span>
              </div>
              {ghostPct > 1.5 && (
                <div
                  className="relative ml-[3px] flex items-center rounded-md border border-dashed border-muted-foreground/30 px-2.5 transition-[width] duration-500"
                  style={{
                    width: `${ghostPct}%`,
                    backgroundImage:
                      "repeating-linear-gradient(45deg, color-mix(in oklch, var(--muted-foreground) 16%, transparent) 0 5px, transparent 5px 10px)",
                  }}
                >
                  <span className="whitespace-nowrap text-[11px] font-semibold tabular-nums text-muted-foreground">
                    +{formatNumeric(offPath)} off-path
                  </span>
                </div>
              )}
            </div>
            {/* Full event-aware reading — kept outside the (width-constrained) bar so it never clips */}
            <p className="mt-1 truncate px-0.5 text-[10px] text-muted-foreground/70">
              {formatNumeric(s.inFunnel)} {visitorsPhrase(s.type, visitorNoun)} in funnel
              {s.type === "form_submission" && s.totalEvents != null && (
                <> · {formatNumeric(s.totalEvents)} total submissions</>
              )}
            </p>
            {i < n - 1 && (
              <div className="flex items-center py-1 pl-0.5">
                <div className="ml-[9px] h-3.5 w-0.5 rounded-sm bg-border/60" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────── VERTICAL (columns) ──
const VBAR_CONFIG: ChartConfig = { inFunnel: { label: "In funnel", color: "var(--chart-1)" } };

function VBarTooltip({
  active,
  payload,
  visitorNoun,
}: {
  active?: boolean;
  payload?: any[];
  visitorNoun?: string;
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const isForm = d.type === "form_submission";
  return (
    <div className="rounded-lg border border-border/30 bg-popover px-3 py-2 text-sm shadow-lg">
      <div className="font-semibold text-foreground">{d.fullName ?? d.name}</div>
      <div className="text-muted-foreground">
        <span className="font-medium tabular-nums text-foreground">{formatNumeric(d.inFunnel)}</span> {visitorsPhrase(d.type, visitorNoun)} in funnel
      </div>
      {isForm && d.totalEvents != null && (
        <div className="text-muted-foreground">
          <span className="font-medium tabular-nums text-foreground">{formatNumeric(d.totalEvents)}</span> total submissions
        </div>
      )}
      <div className="text-xs text-muted-foreground">{d.convLabel} {d.index === 0 ? "(entry)" : "of previous"}</div>
    </div>
  );
}

function VerticalChart({
  steps,
  entered: _entered,
  visitorNoun,
}: {
  steps: FunnelChartStep[];
  entered: number;
  visitorNoun?: string;
}) {
  const keyIndex = biggestDropIndex(steps);
  const data = steps.map((s, i) => {
    const prev = i > 0 ? steps[i - 1].inFunnel : s.inFunnel;
    return {
      name: s.name || `Step ${i + 1}`,
      fullName: s.targetLabel ? `${s.name} · ${s.targetLabel}` : s.name,
      inFunnel: s.inFunnel,
      convLabel: i === 0 ? "100%" : pct(s.inFunnel, prev),
      index: i,
      // Branch/exit steps read muted so they don't look like sequential conversions.
      fill: s.tone === "caution" ? "var(--muted-foreground)" : i === keyIndex ? "var(--primary)" : "var(--primary-tint-ring)",
      type: s.type,
      totalEvents: s.totalEvents,
    };
  });
  return (
    <ChartContainer config={VBAR_CONFIG} className="h-[280px] w-full">
      <BarChart accessibilityLayer data={data} margin={{ top: 28, right: 8, left: 8, bottom: 0 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
        <XAxis
          dataKey="name"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          interval={0}
          tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
          tickFormatter={(v: string) => (v.length > 12 ? v.slice(0, 11) + "…" : v)}
        />
        <YAxis hide domain={[0, "dataMax"]} />
        <Tooltip
          cursor={{ fill: "var(--muted)", opacity: 0.3 }}
          content={<VBarTooltip visitorNoun={visitorNoun} />}
        />
        <Bar dataKey="inFunnel" radius={[6, 6, 0, 0]} maxBarSize={88} isAnimationActive={false}>
          {data.map((d) => (
            <Cell key={d.index} fill={d.fill} />
          ))}
          <LabelList
            dataKey="inFunnel"
            position="top"
            offset={8}
            className="fill-foreground"
            fontSize={12}
            fontWeight={600}
            formatter={(v: number) => formatNumeric(v)}
          />
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

export function FunnelChartView({
  steps,
  entered,
  style,
  className,
  visitorNoun,
}: {
  steps: FunnelChartStep[];
  entered: number;
  style: FunnelChartStyle;
  className?: string;
  /** Overrides the "unique visitors" noun (the admin funnel counts unique users). */
  visitorNoun?: string;
}) {
  if (!steps || steps.length < 2) {
    return (
      <div className="py-12 text-center text-[13px] text-muted-foreground">
        Add at least 2 steps to see the funnel.
      </div>
    );
  }
  return (
    <div className={cn(className)} style={{ ["--mono" as any]: MONO }}>
      {style === "steps" && <StepsChart steps={steps} entered={entered} />}
      {style === "bars" && <BarsChart steps={steps} entered={entered} visitorNoun={visitorNoun} />}
      {style === "list" && (
        <VerticalChart steps={steps} entered={entered} visitorNoun={visitorNoun} />
      )}
    </div>
  );
}
